import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import StatsCard from '../../../components/Cards/StartsCard';
import BarChart from '../../../components/Charts/BarChart';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import {
  Card,
  CardHead,
  CardTitle,
  Hint,
  Input,
  Money,
  Scroll,
  Segmented,
  Select,
  SimpleTable,
  Stack,
  StatsGrid,
} from '../../../components/Layout/PagePieces';
import Page from '../../../components/Page/Page';
import useCostSettings from '../../../hooks/dashboard/profit/useCostSettings';
import useProfitOverview from '../../../hooks/dashboard/profit/useProfitOverview';
import useProfitSummary from '../../../hooks/dashboard/profit/useProfitSummary';
import useUnitEconomics from '../../../hooks/dashboard/profit/useUnitEconomics';
import { ProfitRow } from '../../../models/profit/profit.model';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

import ChatLimitsCard from './ChatLimitsCard';
import CommissionRatesCard from './CommissionRatesCard';
import PricingCard from './PricingCard';
import ProviderChecksCard from './ProviderChecksCard';
import VtpassAlertsCard from './VtpassAlertsCard';

type Tab = 'day' | 'bill_type' | 'user' | 'economics' | 'settings';
const TABS: [Tab, string][] = [
  ['day', 'By day'],
  ['bill_type', 'By bill type'],
  ['user', 'By customer'],
  ['economics', 'Unit economics'],
  ['settings', 'Settings'],
];

const money = (value: string | number) => formatNaira(Number(value));
const signed = (value: string | number) => (
  <Money className={Number(value) < 0 ? 'loss' : Number(value) > 0 ? 'gain' : ''}>{money(value)}</Money>
);

// Profit and loss with everything that costs or earns money (limits spec P5/P6).
const Profit = () => {
  const [tab, setTab] = useState<Tab>('day');
  const { overview, fetchOverview } = useProfitOverview();

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line
  }, []);

  return (
    <Page title='Hethera | Profit'>
      <PageLayout>
        <Stack>
          <PageHeader title='Profit' className='borderless no-bottom-padding' />
          <StatsGrid>
            <Tile title='Profit today' row={overview?.today} />
            <Tile title={`Profit, ${overview?.month.label ?? 'this month'}`} row={overview?.month} />
            <StatsCard.Root>
              <StatsCard.Header>Earned this month</StatsCard.Header>
              <StatsCard.Value>{money(overview?.month.revenue ?? 0)}</StatsCard.Value>
              <StatsCard.PlainFooter>
                Margin {money(overview?.month.purchase_margin ?? 0)} · fees {money(Number(overview?.month.service_fees ?? 0) + Number(overview?.month.gift_fees ?? 0))}
              </StatsCard.PlainFooter>
            </StatsCard.Root>
            <StatsCard.Root>
              <StatsCard.Header>Costs this month</StatsCard.Header>
              <StatsCard.Value>
                {money(Number(overview?.month.llm_cost ?? 0) + Number(overview?.month.whatsapp_cost ?? 0) + Number(overview?.month.paystack_fees ?? 0))}
              </StatsCard.Value>
              <StatsCard.PlainFooter>
                Claude {money(overview?.month.llm_cost ?? 0)} · WhatsApp {money(overview?.month.whatsapp_cost ?? 0)} · Paystack {money(overview?.month.paystack_fees ?? 0)}
              </StatsCard.PlainFooter>
            </StatsCard.Root>
          </StatsGrid>

          <Segmented role='tablist'>
            {TABS.map(([id, label]) => (
              <button key={id} role='tab' aria-selected={tab === id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </Segmented>

          {(tab === 'day' || tab === 'bill_type' || tab === 'user') && <SummaryTab group={tab} />}
          {tab === 'economics' && <EconomicsTab />}
          {tab === 'settings' && <SettingsTab />}
        </Stack>
      </PageLayout>
    </Page>
  );
};

export default Profit;

const Tile: React.FC<{ title: string; row?: ProfitRow }> = ({ title, row }) => (
  <StatsCard.Root>
    <StatsCard.Header>{title}</StatsCard.Header>
    <StatsCard.Value>{signed(row?.profit ?? 0)}</StatsCard.Value>
    <StatsCard.PlainFooter>
      Revenue {money(row?.revenue ?? 0)}
    </StatsCard.PlainFooter>
  </StatsCard.Root>
);

const DAYS = [7, 30, 90];

const SummaryTab: React.FC<{ group: 'day' | 'bill_type' | 'user' }> = ({ group }) => {
  const [days, setDays] = useState(30);
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const { summary, fetchSummary } = useProfitSummary();

  useEffect(() => {
    const to = new Date();
    const from = new Date(to.getTime() - (days - 1) * 86400000);
    fetchSummary(group, from.toISOString().slice(0, 10), to.toISOString().slice(0, 10));
    // eslint-disable-next-line
  }, [group, days]);

  const rows = group === 'user' ? (summary?.rows ?? []).slice(0, 50) : summary?.rows ?? [];
  const chartRows = rows.map((r) => ({ label: r.label, profit: Number(r.profit) }));
  const title = { day: 'Profit by day', bill_type: 'Profit by bill type', user: 'Customers, biggest loss first' }[group];

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>{title}</CardTitle>
          <Hint>
            {summary
              ? `${summary.sessions} conversations, ${summary.sessions_with_purchase} with a purchase, ${summary.purchases} purchases. `
              : ''}
            {group === 'bill_type' ? 'Each conversation’s Claude and WhatsApp costs are split across its purchases by amount.' : ''}
            {group === 'day' ? 'Loss days are highlighted.' : ''}
          </Hint>
        </div>
        <Controls>
          <Segmented>
            {DAYS.map((d) => (
              <button key={d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>
                {d} days
              </button>
            ))}
          </Segmented>
          {group !== 'user' && (
            <Segmented>
              <button className={view === 'chart' ? 'active' : ''} onClick={() => setView('chart')}>Chart</button>
              <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>Table</button>
            </Segmented>
          )}
        </Controls>
      </CardHead>
      {view === 'chart' && group !== 'user' ? (
        <BarChart
          data={chartRows}
          dataKeys={['profit']}
          xAxisDataKey='label'
          fills={['#1F7A45']}
          tooltipLabel={['Profit (₦)']}
          showLegend={false}
          aspect={group === 'day' ? 3 : 2.4}
          isCategorical
        />
      ) : (
        <Scroll>
          <SimpleTable>
            <thead>
              <tr>
                <th>{group === 'day' ? 'Day' : group === 'user' ? 'Customer' : 'Bill type'}</th>
                <th className='num'>Margin</th>
                <th className='num'>Fees</th>
                <th className='num'>Claude</th>
                <th className='num'>WhatsApp</th>
                <th className='num'>Paystack</th>
                <th className='num'>Profit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className={Number(r.profit) < 0 ? 'loss' : ''}>
                  <td>{r.label}</td>
                  <td className='num'>{money(r.purchase_margin)}</td>
                  <td className='num'>{money(Number(r.service_fees) + Number(r.gift_fees))}</td>
                  <td className='num'>{money(r.llm_cost)}</td>
                  <td className='num'>{money(r.whatsapp_cost)}</td>
                  <td className='num'>{money(r.paystack_fees)}</td>
                  <td className='num'>{signed(r.profit)}</td>
                </tr>
              ))}
              {summary && (
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className='num'>{money(summary.total.purchase_margin)}</td>
                  <td className='num'>{money(Number(summary.total.service_fees) + Number(summary.total.gift_fees))}</td>
                  <td className='num'>{money(summary.total.llm_cost)}</td>
                  <td className='num'>{money(summary.total.whatsapp_cost)}</td>
                  <td className='num'>{money(summary.total.paystack_fees)}</td>
                  <td className='num'>{signed(summary.total.profit)}</td>
                </tr>
              )}
            </tbody>
          </SimpleTable>
        </Scroll>
      )}
    </Card>
  );
};

const BILL_ORDER = ['AIRTIME', 'MOBILE_DATA', 'CABLE_TV', 'ELECTRICITY'];
const BILL_LABEL: Record<string, string> = {
  AIRTIME: 'Airtime',
  MOBILE_DATA: 'Data',
  CABLE_TV: 'Cable',
  ELECTRICITY: 'Electricity (amount = what the card pays)',
};

// What one purchase earns at today's pricing (pricing spec P7): it follows
// the Pricing settings, so a change there shows here straight away.
const EconomicsTab = () => {
  const { economics, fetchEconomics } = useUnitEconomics();

  useEffect(() => {
    fetchEconomics();
    // eslint-disable-next-line
  }, []);

  const amounts = economics?.amounts ?? [];
  const rows = economics?.rows ?? [];
  const losing = rows.filter((r) => r.loss_range);
  const p = economics?.pricing;
  return (
    <Stack>
      <Card>
        <CardTitle>At today’s pricing</CardTitle>
        {p && (
          <Hint>
            Airtime up to {money(p.AIRTIME_MAX_PER_PURCHASE)} a purchase. Cable {money(p.SERVICE_FEE_CABLE)} service fee on
            top. Electricity: the card processing fee
            {Number(p.ELECTRICITY_EXTRA_FEE) ? ` plus ${money(p.ELECTRICITY_EXTRA_FEE)}` : ''} comes out of what the
            customer pays. Data loss guard {p.DATA_LOSS_GUARD_ENABLED ? `on (plans must earn ${money(p.DATA_MIN_PROFIT)})` : 'off'}.
            Paystack {Number(p.PAYSTACK_FEE_PCT)}% + {money(p.PAYSTACK_FEE_FLAT)} from {money(p.PAYSTACK_FEE_FLAT_FROM)},
            capped at {money(p.PAYSTACK_FEE_CAP)}. Change these under Settings → Pricing.
          </Hint>
        )}
        <Verdict className={losing.length ? 'loss' : 'gain'}>
          {economics == null
            ? ''
            : losing.length
            ? `${losing.length} of ${rows.length} products still lose money at some amounts we sell: ${losing
                .map((r) => r.service_id + (r.meter_class !== 'ANY' ? ` (${r.meter_class})` : ''))
                .join(', ')}.`
            : `No product loses money at any amount we sell, before chat and WhatsApp costs.`}
        </Verdict>
      </Card>
      <Card>
        <CardTitle>What one purchase earns, before chat costs</CardTitle>
        <Hint>
          VTpass commission (from the stored rates) plus our fee, minus Paystack’s fee, at each amount. “Not sold” is above
          the airtime cap or refused by the data loss guard. Real purchases from the last 90 days are beside each product.
        </Hint>
        <Scroll>
          <SimpleTable>
            <thead>
              <tr>
                <th>Product</th>
                <th>Priced as</th>
                <th className='num'>Rate</th>
                {amounts.map((a) => (
                  <th key={a} className='num'>
                    {money(a)}
                  </th>
                ))}
                <th>Where it loses</th>
                <th className='num'>Purchases</th>
                <th className='num'>Average</th>
                <th className='num'>Lost money</th>
              </tr>
            </thead>
            {BILL_ORDER.map((bill) => {
              const group = rows.filter((r) => r.bill_type === bill);
              if (!group.length) return null;
              return (
                <tbody key={bill}>
                  <tr>
                    <GroupCell colSpan={amounts.length + 7}>{BILL_LABEL[bill]}</GroupCell>
                  </tr>
                  {group.map((r) => (
                    <tr key={`${r.service_id}-${r.meter_class}`}>
                      <td>
                        {r.service_id}
                        {r.meter_class !== 'ANY' ? ` (${r.meter_class})` : ''}
                      </td>
                      <td>{r.pricing}</td>
                      <td className='num'>
                        {Number(r.rate_pct)}%{r.cap ? `, cap ${money(r.cap)}` : ''}
                      </td>
                      {amounts.map((a) => (
                        <td key={a} className='num'>
                          {r.not_sold_at[a] ? (
                            <NotSold title={r.not_sold_at[a] === 'cap' ? 'Above the airtime cap' : 'Refused by the data loss guard'}>
                              Not sold
                            </NotSold>
                          ) : r.profit_at[a] != null ? (
                            signed(r.profit_at[a] as string)
                          ) : (
                            '—'
                          )}
                        </td>
                      ))}
                      <td>
                        {r.loss_range ? (
                          <Money className='loss'>
                            {money(r.loss_range[0])}–{money(r.loss_range[1])}
                          </Money>
                        ) : (
                          <Money className='gain'>Never</Money>
                        )}
                        {r.not_sold_range && (
                          <NotSold>
                            {' '}
                            · not sold {money(r.not_sold_range[0])}–{money(r.not_sold_range[1])}
                          </NotSold>
                        )}
                      </td>
                      <td className='num'>{r.purchases || '—'}</td>
                      <td className='num'>{r.average_amount ? money(r.average_amount) : '—'}</td>
                      <td className='num'>{r.loss_share_pct != null ? `${r.loss_share_pct}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </SimpleTable>
        </Scroll>
      </Card>
    </Stack>
  );
};

const Verdict = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  font-weight: 500;

  &.loss {
    color: ${({ theme }) => theme.palette.errorRed};
  }
  &.gain {
    color: ${({ theme }) => theme.palette.greenColor};
  }
`;

const NotSold = styled.span`
  color: var(--muted);
  font-size: 12px;
  white-space: nowrap;
`;

const GroupCell = styled.td`
  background: var(--panel);
  font-weight: 600;
  font-size: 12px;
`;

const costSchema = Yup.object({
  rate: Yup.number().moreThan(0).lessThan(100000).required('Required'),
  currency: Yup.string().oneOf(['USD', 'NGN']).required(),
  utility: Yup.number().min(0).required('Required'),
  marketing: Yup.number().min(0).required('Required'),
  authentication: Yup.number().min(0).required('Required'),
  service: Yup.number().min(0).required('Required'),
  llm: Yup.string()
    .required('Required')
    .test('json', 'Use {"model": [input, output, cache write, cache read]}', (value) => {
      try {
        const parsed = JSON.parse(value ?? '');
        return Object.values(parsed).every((p) => Array.isArray(p) && p.length === 4);
      } catch {
        return false;
      }
    }),
});

const SettingsTab = () => {
  const { settings, saving, fetchSettings, save } = useCostSettings();

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  const values = settings?.values;
  const [model, setModel] = useState('');
  useEffect(() => {
    setModel(values?.AGENT_MODEL ?? '');
  }, [values?.AGENT_MODEL]);
  const modelOptions = Array.from(
    new Set([...(values?.AGENT_MODEL ? [values.AGENT_MODEL] : []), ...Object.keys(values?.LLM_PRICES_USD_PER_MTOK ?? {})])
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: costSchema,
    initialValues: {
      rate: values?.COST_USD_NGN_RATE ?? 0,
      currency: values?.WHATSAPP_RATE_CURRENCY ?? 'USD',
      utility: values?.WHATSAPP_RATES?.utility ?? 0,
      marketing: values?.WHATSAPP_RATES?.marketing ?? 0,
      authentication: values?.WHATSAPP_RATES?.authentication ?? 0,
      service: values?.WHATSAPP_RATES?.service ?? 0,
      llm: JSON.stringify(values?.LLM_PRICES_USD_PER_MTOK ?? {}, null, 0),
    },
    onSubmit: (v) =>
      save(
        {
          COST_USD_NGN_RATE: Number(v.rate),
          WHATSAPP_RATE_CURRENCY: v.currency,
          WHATSAPP_RATES: { utility: Number(v.utility), marketing: Number(v.marketing), authentication: Number(v.authentication), service: Number(v.service) },
          LLM_PRICES_USD_PER_MTOK: JSON.parse(v.llm),
        },
        () => toast.success('Saved. New cost events use these; past ones keep theirs.')
      ),
  });

  const field = (name: keyof typeof formik.values, label: string, step = 'any') => (
    <Field>
      <span>{label}</span>
      <Input name={name} type='number' step={step} value={formik.values[name] as any} onChange={formik.handleChange} />
      {formik.errors[name] && <ErrorText>{String(formik.errors[name])}</ErrorText>}
    </Field>
  );

  return (
    <>
      <PricingCard />
      <VtpassAlertsCard />

      <Card>
        <CardTitle>Chat model</CardTitle>
        <Hint>
          The Claude model that answers customers on WhatsApp. Only models with a Claude price below can be picked: to try a
          new one, add its price under Cost inputs first. A change applies from the next message.
          {values?.AGENT_MODEL && settings?.file_defaults?.AGENT_MODEL !== values.AGENT_MODEL
            ? ` The settings file default is ${settings?.file_defaults?.AGENT_MODEL}.`
            : ''}
        </Hint>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (model && model !== values?.AGENT_MODEL)
              save({ AGENT_MODEL: model }, () => toast.success(`Customers are now answered by ${model}.`));
          }}
        >
          <Field>
            <span>Model</span>
            <Select value={model} onChange={(e) => setModel(e.target.value)}>
              {modelOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                  {m === values?.AGENT_MODEL ? ' (in use)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Actions>
            <PrimaryBlackButton type='submit' disabled={saving || !model || model === values?.AGENT_MODEL}>
              {saving ? 'Saving…' : 'Use this model'}
            </PrimaryBlackButton>
          </Actions>
        </form>
      </Card>

      <ChatLimitsCard />

      <Card>
        <CardTitle>Cost inputs</CardTitle>
        <Hint>
          Use the exchange rate your card statement shows for the Anthropic and Meta invoices, bank margin included. A
          change applies to new cost events only.
          {settings?.updated_at ? ` Last changed ${tableFormatDateTimeNG(settings.updated_at)} by ${settings.updated_by}.` : ''}
        </Hint>
        <form onSubmit={formik.handleSubmit}>
          <FieldGrid>
            {field('rate', '₦ per US$')}
            <Field>
              <span>WhatsApp rate currency</span>
              <Select name='currency' value={formik.values.currency} onChange={formik.handleChange}>
                <option value='USD'>USD</option>
                <option value='NGN'>NGN</option>
              </Select>
            </Field>
            {field('utility', 'Utility message')}
            {field('marketing', 'Marketing message')}
            {field('authentication', 'Authentication message')}
            {field('service', 'Service message (billed)')}
          </FieldGrid>
          <Field>
            <span>Claude prices, US$ per million tokens</span>
            <Input name='llm' value={formik.values.llm} onChange={formik.handleChange} />
            {formik.errors.llm && <ErrorText>{formik.errors.llm}</ErrorText>}
          </Field>
          <Actions>
            <PrimaryBlackButton type='submit' disabled={saving}>
              {saving ? 'Saving…' : 'Save cost inputs'}
            </PrimaryBlackButton>
          </Actions>
        </form>
        {!!settings?.history.length && (
          <Scroll>
            <SimpleTable>
              <thead>
                <tr>
                  <th>Changed</th>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {settings.history.slice(0, 10).map((h, i) => (
                  <tr key={i}>
                    <td>{tableFormatDateTimeNG(h.changed_at)}</td>
                    <td>{h.key}</td>
                    <td>
                      <code>{JSON.stringify(h.value?.value)}</code>
                    </td>
                    <td>{h.changed_by}</td>
                  </tr>
                ))}
              </tbody>
            </SimpleTable>
          </Scroll>
        )}
      </Card>

      <ProviderChecksCard />

      <CommissionRatesCard />
    </>
  );
};

const Controls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 12px;
`;

const Actions = styled.div`
  margin-top: 12px;
`;
